<template>
  <div
    ref="$productActions"
    class="osk-product-actions"
  >
    <div
      v-if="isCurrentUserSeller"
      class="osk-product-actions__content"
    >
      <UikitButton
        full-width
        :to="`/publication?product=${product.productId}`"
        :size="buttonSize"
        :disabled="!canBeEdited"
        style="margin-top: 8px;"
      >
        {{ canBeEdited ? $t('common.edit') : $t('components.products.productActions.onModeration') }}
      </UikitButton>
    </div>

    <template
      v-else
    >
      <UikitFormFieldCaption
        :message="isError ? $t('components.products.productActions.selectSize') : undefined"
        error
        class="osk-field_error"
      />

      <div class="osk-product-actions__content">
        <UikitButton
          full-width
          :loading="loading"
          :disabled="isAdded || isDeactivated || (isInOCommunitySelection && isLiked)"
          :size="buttonSize"
          :color="isInOCommunitySelection ? 'secondary' : undefined"
          @click="onButtonClick"
        >
          <span v-if="isAdded">{{ $t('components.products.productActions.productInCart') }}</span>
          <span v-else-if="product.productState === 'SOLD'">
            {{ $t('components.products.productActions.productSold') }}
          </span>
          <span v-else-if="isInOCommunitySelection">Добавить в вишлист</span>
          <span v-else-if="product.productState !== 'PUBLISHED'">
            {{ $t('components.products.productActions.productUnavailable') }}
          </span>
          <span v-else>{{ $t('components.products.productActions.toCart') }}</span>
        </UikitButton>

        <UikitUserLikesCount
          v-if="!isInOCommunitySelection"
          class="osk-product-actions__wishlist"
          :is-liked="isLiked"
          :product-id="product.productId!"
          :likes-count="product.likesCount"
          :analytics="{ value: getCommunity(product) }"
          is-hide-zero
          color-white
          is-button
          @like="emits('liked', true)"
          @dislike="emits('liked', false)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { AnalyticsPages } from '~/types/analytics';
import { ProductDTORes } from '~/restAPI/data-contracts';
import { getCommunity } from '~/services/analytics/oskellyAnalytics';
import { scrollToFirstError } from '@/utils/scroll';
import { useProductEditingRestrictions } from '~/composables/useProductEditingRestrictions';
import { useNotification } from '@kyvg/vue3-notification';

const props = defineProps<{
  product: ProductDTORes
  isSizes: boolean
  sizeId?: number
  isLiked?: boolean
}>();

const emits = defineEmits<{
  (e: 'liked', value: boolean): void,
}>();

const cartStore = useCartStore();
const { isDesktop, isMounted } = useUiBreakpoints();
const { isAuth, account, openAuthDialog } = useAccountStore();
const { toggleLike } = useWishlistStore();
const { $addEvent } = useNuxtApp();
const { notify } = useNotification();

const $productActions = ref<HTMLElement>();
const isAdded = ref(false);
let currentPositionId: number | null = null;

const loading = ref(false);
const isError = ref(false);
const isInOCommunitySelection = computed(() => !!props.product.exclusiveSelectionTimeForLowStatuses);

const { canProductBeEdited } = useProductEditingRestrictions();

const canBeEdited = canProductBeEdited(props.product.productState);
const isDeactivated = computed(() => props.product.productState !== 'PUBLISHED' && !isInOCommunitySelection.value);
const isNoSizes = computed(() => !props.isSizes);
const productId = computed(() => props.product.productId);
const sellerId = computed(() => props.product.seller?.id);
const disabled = computed(() => Boolean(!isNoSizes.value && props.sizeId === undefined));
const isCurrentUserSeller = computed<boolean>(() => (isAuth.value ? account.value.id === props.product.seller!.id : false));
const buttonSize = computed(() => {
  if (!isMounted.value || isDesktop.value) return 'm';

  return 'l';
});

async function addToCart() {
  if (disabled.value) return false;

  try {
    await cartStore.addToCart({ productId: productId.value!, sizeId: props.sizeId }, props.product);

    loading.value = false;
    return true;
  } catch (error) {
    const errorMessage = error?.response?.data?.humanMessage;

    if (errorMessage) {
      notify({
        group: 'mainNotifications',
        type: 'error',
        text: errorMessage,
      });
    }

    isToggling.value = false;
    loading.value = false;
    return false;
  }
}

async function toggleItemAtCart() {
  if (disabled.value) {
    isError.value = true;
    await nextTick();

    scrollToFirstError($productActions.value, isDesktop.value ? 264 : 176);
    return;
  }

  if (loading.value || isAdded.value) return;

  loading.value = true;

  const isOk = await addToCart();

  if (isOk) navigateTo('/cart');
}

function checkIsItemAddedToCart() {
  const { cartOrders } = cartStore;

  const currentOrder = cartOrders.value.find((group) => group.seller!.id === sellerId.value);

  if (!currentOrder) {
    if (isAdded.value) {
      isAdded.value = false;
    }
    if (currentPositionId) {
      currentPositionId = null;
    }
    return;
  }

  const currentItem = currentOrder.items!.find(
    (item) => item.productId === productId.value && (isNoSizes.value ? true : props.sizeId === item.size!.id),
  );

  if (!currentItem) {
    if (isAdded.value) {
      isAdded.value = false;
    }
    if (currentPositionId) {
      currentPositionId = null;
    }
    return;
  }

  currentPositionId = currentItem.id!;

  if (!isAdded.value) {
    isAdded.value = true;
  }
}
async function addToWishlist() {
  if (!isAuth.value) {
    openAuthDialog();

    return;
  }

  loading.value = true;

  try {
    await toggleLike(props.product.productId!, false, undefined, undefined, {
      action: 'add_wishlist',
      page: AnalyticsPages.ProductPage,
      value: isInOCommunitySelection.value ? 'community' : undefined,
    });

    emits('liked', true);
  } finally {
    loading.value = false;
  }
}
function onButtonClick() {
  if (isInOCommunitySelection.value) {
    addToWishlist();
  } else {
    toggleItemAtCart();
  }
}

watch(() => [
  cartStore.cartOrders.value,
  props.sizeId,
], () => {
  checkIsItemAddedToCart();
}, { immediate: true });

watch(() => props.sizeId, (value) => {
  if (value) {
    isError.value = false;
  }
});
</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";

.osk-product-actions {
  &__content {
    display: flex;
    position: fixed;
    bottom: 74px;
    left: 0;
    width: 100%;
    padding: 16px;
    background: #fff;
    z-index: 15;
    border-bottom: 1px solid $grey-3;

    @include media-query(lg-and-up) {
      position: static;
      padding: 0;
      border-bottom: 0;
      margin-top: $indent-small;
    }
  }

  &__wishlist {
    margin-left: $indent-small;
    display: none;

    @include media-query(lg-and-up) {
      display: inline-flex;
    }
  }
}
</style>
